<script lang="ts">
    import { onMount } from "svelte";
    import { _ } from "svelte-i18n";
    import { createEventDispatcher } from "svelte";
    import Dialog, { Title as DTitle, Content as Content, Actions, InitialFocus } from "@smui/dialog";
    import DataTable, { Body, Row, Cell } from "@smui/data-table";
    import Button, { Label } from "@smui/button";
    import { Icon } from "@smui/icon-button";
    import Textfield from "@smui/textfield";
    import Flex from "svelte-flex";
    import ChipInput from "../common/ChipInput.svelte";
    import CircularProgress from "@smui/circular-progress";

    import DTInputCell from "../common/DTInputCell.svelte";
    import DTSelectCell from "../common/DTSelectCell.svelte";

    import { selectedWeek } from "../../../stores/AppStatus";
    import { countryList, currencyList } from "../../../stores/AppConfig";
    import { loadQuoteSheet } from "../Load/QuoteSheet.js";
    import { loadSupplierProducts as getProductSuppliers } from "../Load/Supplier";
    import { nvl, getFieldErrMsg, existsError, clearError, updateErrors } from "../Upload/Upload.js";
    import {
        validateVolume,
        validatePrice,
        validateCartonLogistics,
        validatePalletLogistics,
        validateCountryOfOriginCode,
        validatePriceIsNotEmpty,
    } from "../Upload/ValidateQuoteSheet";

    export let item: any;
    export let open;
    export let title;
    let headerColors = "tescoblue white-text";
    let big = true;

    let currencyListLoc = [];
    let countries = ["Cz", "Sk", "Hu"];
    let hasValidated = false;

    // Variables for product
    let itemPrivilage: boolean = false;
    let productsFetching = false;
    let productMap = new Map();
    let productTemplateMap = new Map();
    let itemCheck: any;

    // Supplier variables
    let supplierOptions = [];
    let suppliersFetching = false;

    let itemDuplicityLoadCheck = false;

    let buttonSaveEnabled = false;

    // Country variables options and text
    let countryChips = [];
    let countryOptions = [...$countryList]
        .filter((e) => e.active)
        .map((x) => {
            return { code: x.code, name: x.description, text: x.code + " - " + x.description };
        });

    const dispatch = createEventDispatcher();

    $: buttonSaveEnabled =
        (!suppliersFetching &&
            !productsFetching &&
            !itemDuplicityLoadCheck &&
            item &&
            (item.errors == null || (Array.isArray(item.errors) && item.errors.length === 0)) &&
            item?.countryOfOrigin &&
            item?.volume &&
            item?.supplierId != null &&
            (item?.priceCz != null || item?.priceSk != null || item?.priceHu != null)) ??
        false;

    $: productNotSelected = !item || (item && (!item?.productId || item?.productId == ""));

    let productInfo = `${item.productId} - ${item.productDesc}`;

    const DEFAULT_ERRORS = [
        {
            priority: 6,
            message: "missing_country_of_origin_code",
            field: "countryOfOrigin",
            type: "E",
        },
        {
            priority: 4,
            message: "missing_volume",
            field: "volume",
            type: "E",
        },
        {
            priority: 4,
            message: "no_price_is_entered_for_product",
            field: "price",
            type: "E",
        },
    ];

    //CoO Chips
    // Search CoO name and code
    function searchCountries(input) {
        if (!input) return [];
        const lowerInput = input.toLowerCase();
        return countryOptions.filter(
            (option) =>
                (option.name.toLowerCase().includes(lowerInput) || option.code.toLowerCase().includes(lowerInput)) && !countryChips.some((chip) => chip.code === option.code),
        );
    }

    // Update countryOfOrigin when chips changes
    function handleChipChange(event) {
        const { type, detail } = event; // Extract type and detail

        if (type === "add") {
            // Ensure chip is added to countryChips only once
            if (!countryChips.some((chip) => chip.code === detail.code)) {
                countryChips = [...countryChips, detail];
            }
        } else if (type === "remove") {
            // Remove the chip from countryChips
            countryChips = countryChips.filter((chip) => chip.code !== detail.code);
        }

        // Update countryOfOrigin using countryChips
        item.countryOfOrigin = countryChips
            .map((chip) => chip.code)
            .sort()
            .join(", ");

        // Perform validations
        validateCountryOfOrigin();
        validateQsExist();
    }

    async function qsExistLoad() {
        console.log("====================== qsExistLoad =========================");
        try {
            return await loadQuoteSheet(item.year, item.week, item.supplierId, item.productId, item.countryOfOrigin, item.variety, item.cartonLogistics, item.palletLogistics);
        } catch (error) {
            console.error("Error fetching Supplier autocomplete data:", error);
            return [];
        }
    }

    async function validateQsExist() {
        console.log("=== validateQsExist() ===");

        if (!item || !item.supplierId || !item.productId || !item.countryOfOrigin) {
            console.log("Skipping validateQsExist validation");
            return;
        }

        itemDuplicityLoadCheck = true;

        clearError(item, "quoteSheetExists");
        let errors = [];
        let fields = [];

        const qs = await qsExistLoad();

        if (qs && qs.length > 0 && qs[0].id && item.id != qs[0].id) {
            const qsData = qs[0];

            errors.push({ priority: 3, message: "quote_sheet_exists_already", field: "quoteSheetExists", type: "E", qsId: qsData.id });
            fields.push("quoteSheetExists");
        }

        // Update errors in the item
        if (errors.length > 0) {
            item = updateErrors(item, item.errors || [], item.warnings || [], fields, errors);
        }

        itemDuplicityLoadCheck = false;
    }

    function validateCountryOfOrigin() {
        console.log("=== validateCountryOfOrigin() ===");
        clearError(item, "countryOfOrigin");
        let errors = [];
        let fields = [];

        // Perform country of origin validation
        errors.push(validateCountryOfOriginCode(item, countryOptions));
        fields.push("countryOfOrigin");

        // Update errors in the item
        if (errors.length > 0) {
            console.log({ errors });
            item = updateErrors(item, item.errors || [], item.warnings || [], fields, errors);
        }
    }

    function validateCarton() {
        const original = productTemplateMap.get(item.supplierId);
        clearError(item, "cartonLogistics");

        const errors = [validateCartonLogistics(item, original)];
        item = updateErrors(item, item.errors ?? [], item.warnings ?? [], ["cartonLogistics"], errors);
    }

    function validatePallet() {
        const original = productTemplateMap.get(item.supplierId);
        clearError(item, "palletLogistics");

        const errors = [validatePalletLogistics(item, original)];
        item = updateErrors(item, item.errors ?? [], item.warnings ?? [], ["palletLogistics"], errors);
    }

    function validateVolumeField() {
        console.log("=== validateVolumeField() ===");
        clearError(item, "volume");
        let errors = [];
        let fields = [];

        // Perform volume validation
        errors.push(validateVolume(item));
        fields.push("volume");

        // Update errors in the item
        if (errors.length > 0) {
            item = updateErrors(item, item.errors || [], item.warnings || [], fields, errors);
        }
    }

    function validateInputPrice() {
        if (!hasValidated) {
            hasValidated = true; // delay as on:change and on:input do not trigger in the same time

            console.log("=== validateInputPrice() ===", { item });
            clearError(item, "price");
            let errors = [];
            let fields = [];

            errors.push(validatePriceIsNotEmpty(item, item));
            fields.push("price");

            for (let cntr of countries) {
                clearError(item, "price" + cntr);
                clearError(item, "pricePromo" + cntr);

                errors.push(validatePrice(item, item, cntr.toUpperCase(), "REGULAR"));
                fields.push("price" + cntr);

                errors.push(validatePrice(item, item, cntr.toUpperCase(), "PROMO"));
                fields.push("pricePromo" + cntr);
            }

            // Update errors in the item
            if (errors.length > 0) {
                item = updateErrors(item, item.errors || [], item.warnings || [], fields, errors);
            }

            setTimeout(() => {
                hasValidated = false;
            }, 50);
        }
    }

    onMount(async () => {
        const list = await getProductSuppliers(null, item.productId, 1);
        if (list && Array.isArray(list) && list.length > 0) {

            productTemplateMap = new Map(list.map((p) => [p.supplierId, Object.freeze(structuredClone(p))]));

            item = buildEditableItem(productTemplateMap.get(item.supplierId) ?? list[0]);

            // defaults that belong only to the dialog
            item.fileName = "via purchase screen";
            item.errors = DEFAULT_ERRORS.slice();
            item.year = $selectedWeek.tesco_year;
            item.week = $selectedWeek.tesco_week;

            supplierOptions = list.map(({ supplierId: id, supplierName: name }) => ({
                id,
                name,
                text: `${id} – ${name}`,
            }));
        }
    });

    function handleSupplierChange() {
        if (!item.supplierId) return;
        const tpl = productTemplateMap.get(item.supplierId);
        const edit = buildEditableItem(tpl);

        item = edit;
        countryChips = edit.helpChips;
        validateInputPrice();
        validateCountryOfOrigin();
    }

    function buildEditableItem(template) {
        console.log("buildEditableItem", template);
        const cloned = structuredClone(template);
        console.log("countryOfOrigin", cloned.countryOfOrigin);

        const chips = (cloned.countryOfOrigin ?? "")
            .split(",")
            .map((c) => c.trim())
            .filter(Boolean)
            .map((code) => {
                const opt = countryOptions.find((o) => o.code === code);
                return opt ? { code, name: opt.name } : { code, name: code };
            });

        return {
            ...cloned,

            // -----------------------------------------------------------
            // Immutable for comparing - validation
            productCartonLogistics: cloned.cartonLogistics,
            productPalletLogistics: cloned.palletLogistics,
            // -----------------------------------------------------------

            fileName: "via purchase screen",
            priceCz: null,
            priceSk: null,
            priceHu: null,
            pricePromoCz: null,
            pricePromoSk: null,
            pricePromoHu: null,
            year: $selectedWeek.tesco_year,
            week: $selectedWeek.tesco_week,
            errors: DEFAULT_ERRORS.slice(),
            helpChips: chips,
        };
    }
</script>

<Dialog
    data-qa="ps-add-qs-dialog"
    bind:open
    scrimClickAction=""
    escapeKeyAction=""
    aria-labelledby="data-changed-title"
    aria-describedby="data-changed-content"
    class="pa-2"
    surface$style={big ? "width: 850px; max-width: calc(100vw - 32px);" : ""}
>
    <DTitle id="data-changed-title" class={headerColors}>{title}</DTitle>
    <Content>
        <div>
            {#if suppliersFetching}
                <Flex class="w-100" style="height: 500px;">
                    <CircularProgress style="height: 96px; width: 96px;" indeterminate />
                </Flex>
            {:else if item}
                <DataTable class="w-100 input-form no-border pa-2">
                    <Body>
                        <Row>
                            <Cell class="form-label">{$_("product")}</Cell>
                            <DTInputCell colspan="4" value={productInfo} editable={false} numeric={false} class="fs-1rem tescoblue-text grey lighten-2" />
                        </Row>
                        <Row>
                            <Cell class="form-label">{$_("supplier")}</Cell>
                            <td colspan="4">
                                <DTSelectCell
                                    bind:value={item.supplierId}
                                    list={supplierOptions}
                                    on:change={handleSupplierChange}
                                    format={{ showId: true, translateName: false }}
                                    class="w-100"
                                ></DTSelectCell>
                            </td>
                        </Row>
                        <Row>
                            <Cell class="form-label">{$_("hierarchy")}</Cell>
                            <DTInputCell colspan="4" bind:value={item.hierarchyClassDesc} editable={false} numeric={false} class="fs-1rem tescoblue-text grey lighten-2" />
                        </Row>
                        <Row>
                            <Cell class="form-label">{$_("units_of_sale")}</Cell>
                            <DTInputCell colspan="4" value={$_(nvl(item.units, ""))} editable={false} numeric={false} class="fs-1rem tescoblue-text grey lighten-2" />
                        </Row>
                        <Row>
                            <Cell class="form-label">{$_("carton_logistics")}</Cell>
                            <td colspan="4">
                                <Textfield
                                    variant="outlined"
                                    type="text"
                                    bind:value={item.cartonLogistics}
                                    withTrailingIcon={existsError(item, "cartonLogistics", "E") || existsError(item, "cartonLogistics", "W")}
                                    invalid={existsError(item, "cartonLogistics", "E")}
                                    input$maxlength={3}
                                    input$class="tescoblue-text fs-1rem"
                                    on:input={() => {
                                        validateCarton();
                                        validateQsExist();
                                    }}
                                    on:focus={(event) => {
                                        event.detail.target.select();
                                    }}
                                    required
                                    class="w-100"
                                >
                                    <svelte:fragment slot="trailingIcon">
                                        {#if existsError(item, "cartonLogistics", "E")}
                                            <Icon class="material-icons red-text  mdc-text-field__icon mdc-text-field__icon--trailing">error</Icon>
                                        {:else if existsError(item, "cartonLogistics", "W")}
                                            <Icon class="material-icons orange-text  mdc-text-field__icon mdc-text-field__icon--trailing">warning</Icon>
                                        {/if}
                                    </svelte:fragment>
                                </Textfield>
                            </td>
                        </Row>

                        {#if existsError(item, "cartonLogistics", "E")}
                            <Row class="helpertext__row">
                                <Cell></Cell>
                                <Cell colspan={4} style="padding: 0">
                                    <div class="red-text fs-08rem">
                                        {$_(getFieldErrMsg(item, "cartonLogistics", "E"))}
                                    </div>
                                </Cell>
                            </Row>
                        {:else if existsError(item, "cartonLogistics", "W")}
                            <Row class="helpertext__row">
                                <Cell></Cell>
                                <Cell colspan={4} style="padding: 0">
                                    <div class="orange-text fs-08rem">
                                        {$_("carton_logistics_different_from_product_definition_extended", {
                                            values: { productValue: item.productCartonLogistics },
                                        })}
                                    </div>
                                </Cell>
                            </Row>
                        {/if}
                        <Row>
                            <Cell class="form-label">{$_("pallet_logistics")}</Cell>
                            <td colspan="4">
                                <Textfield
                                    variant="outlined"
                                    type="text"
                                    bind:value={item.palletLogistics}
                                    withTrailingIcon={existsError(item, "palletLogistics", "E") || existsError(item, "palletLogistics", "W")}
                                    invalid={existsError(item, "palletLogistics", "E")}
                                    input$maxlength={3}
                                    input$emptyValueUndefined
                                    input$class="tescoblue-text fs-1rem"
                                    on:input={() => {
                                        validatePallet();
                                        validateQsExist();
                                    }}
                                    on:focus={(event) => {
                                        event.detail.target.select();
                                    }}
                                    required
                                    class="w-100"
                                >
                                    <svelte:fragment slot="trailingIcon">
                                        {#if existsError(item, "palletLogistics", "E")}
                                            <Icon class="material-icons red-text mdc-text-field__icon mdc-text-field__icon--trailing">error</Icon>
                                        {:else if existsError(item, "palletLogistics", "W")}
                                            <Icon class="material-icons orange-text mdc-text-field__icon mdc-text-field__icon--trailing">warning</Icon>
                                        {/if}
                                    </svelte:fragment>
                                </Textfield>
                            </td>
                        </Row>
                        {#if existsError(item, "palletLogistics", "E")}
                            <Row class="helpertext__row">
                                <Cell></Cell>
                                <Cell colspan={4} style="padding: 0">
                                    <div class="red-text fs-08rem">
                                        {$_(getFieldErrMsg(item, "palletLogistics", "E"))}
                                    </div>
                                </Cell>
                            </Row>
                        {:else if existsError(item, "palletLogistics", "W")}
                            <Row class="helpertext__row">
                                <Cell></Cell>
                                <Cell colspan={4} style="padding: 0">
                                    <div class="orange-text fs-08rem">
                                        {$_("pallet_logistics_different_from_product_definition_extended", {
                                            values: { productValue: item.productPalletLogistics },
                                        })}
                                    </div>
                                </Cell>
                            </Row>
                        {/if}
                        <Row>
                            <Cell class="form-label">{$_("country_of_origin")}</Cell>
                            <td colspan="4">
                                <ChipInput
                                    class="edit-cell-border"
                                    autocomplete$class="pa-2"
                                    bind:chips={countryChips}
                                    key={(country) => country.code}
                                    getChipLabel={(country) => `${country.name} (${country.code})`}
                                    getChipText={(country) => country.name}
                                    autocomplete$search={searchCountries}
                                    autocomplete$getOptionLabel={(country) => `${country.name} (${country.code})`}
                                    input$disabled={countryChips.length >= 3}
                                    chipTrailingAction$class="material-icons"
                                    chipTrailingAction$aria-label="Remove country"
                                    on:add={handleChipChange}
                                    on:remove={handleChipChange}
                                >
                                    <svelte:fragment slot="chipTrailingAction">cancel</svelte:fragment>
                                    <svelte:fragment slot="label">{countryChips.length >= 3 ? $_("invalid_count_of_coo_codes") : $_("search")}</svelte:fragment>
                                    <CircularProgress slot="loading" style="height: 24px; width: 24px;" indeterminate />
                                </ChipInput>
                            </td>
                        </Row>
                        {#if existsError(item, "countryOfOrigin", "E")}
                            <Row class="helpertext__row">
                                <Cell></Cell>
                                <Cell colspan={4} style="padding: 0">
                                    <div class="red-text fs-08rem">
                                        {!productNotSelected && (item?.countryOfOrigin == null || item?.countryOfOrigin == "")
                                            ? $_("field_is_required")
                                            : $_(getFieldErrMsg(item, "countryOfOrigin", "E"))}
                                    </div>
                                </Cell>
                            </Row>
                        {:else if existsError(item, "countryOfOrigin", "W")}
                            <Row class="helpertext__row">
                                <Cell></Cell>
                                <Cell colspan={4} style="padding: 0">
                                    <div class="orange-text fs-08rem">
                                        {$_(getFieldErrMsg(item, "countryOfOrigin", "W"))}
                                    </div>
                                </Cell>
                            </Row>
                        {/if}
                        <Row>
                            <Cell class="form-label">{$_("variety")}</Cell>
                            <DTInputCell
                                colspan="4"
                                bind:value={item.variety}
                                editable={!productNotSelected}
                                numeric={false}
                                class="fs-1rem tescoblue-text edit-cell-border rounded"
                                on:change={() => {
                                    validateQsExist();
                                }}
                            ></DTInputCell>
                        </Row>
                        {#if existsError(item, "quoteSheetExists", "E")}
                            <Row class="helpertext__row">
                                <Cell></Cell>
                                <Cell colspan={4} style="padding: 0">
                                    <div class="red-text fs-08rem">
                                        {$_(getFieldErrMsg(item, "quoteSheetExists", "E"))}
                                    </div>
                                </Cell>
                            </Row>
                        {/if}
                        <Row>
                            <Cell class="form-label">{$_("volume")}</Cell>
                            <td colspan="4">
                                <Textfield
                                    variant="outlined"
                                    type="text"
                                    disabled={productNotSelected}
                                    bind:value={item.volume}
                                    withTrailingIcon={existsError(item, "volume", "E")}
                                    invalid={existsError(item, "volume", "E") || (!productNotSelected && (item?.volume == null || item?.volume == ""))}
                                    input$maxlength={8}
                                    input$autocomplete="Nothing"
                                    input$emptyValueUndefined
                                    input$class="tescoblue-text fs-1rem"
                                    on:input={() => {
                                        validateVolumeField();
                                    }}
                                    on:focus={(event) => {
                                        event.detail.target.select();
                                    }}
                                    required
                                    class="w-100 tescoblue-text edit-cell-border"
                                >
                                    <svelte:fragment slot="trailingIcon">
                                        {#if existsError(item, "volume", "E") || item?.volume == null || item?.volume == ""}
                                            <Icon class="material-icons red-text mdc-text-field__icon mdc-text-field__icon--trailing">error</Icon>
                                        {/if}
                                    </svelte:fragment>
                                </Textfield>
                            </td>
                        </Row>
                        {#if existsError(item, "volume", "E")}
                            <Row class="helpertext__row">
                                <Cell></Cell>
                                <Cell colspan={4} style="padding: 0">
                                    <div class="red-text fs-08rem">
                                        {!productNotSelected && (item?.volume == null || item?.volume == "") && itemPrivilage
                                            ? $_("field_is_required")
                                            : $_(getFieldErrMsg(item, "volume", "E"))}
                                    </div>
                                </Cell>
                            </Row>
                        {:else if existsError(item, "volume", "W")}
                            <Row class="helpertext__row">
                                <Cell></Cell>
                                <Cell colspan={4} style="padding: 0">
                                    <div class="orange-text fs-08rem">
                                        {$_(getFieldErrMsg(item, "volume", "W"))}
                                    </div>
                                </Cell>
                            </Row>
                        {/if}
                        {#each countries as cntry}
                            {#if item && item["currency" + cntry] && item["currency" + cntry] != ""}
                                {@const hasPriceError = existsError(item, "price" + cntry, "E")}
                                {@const hasPromoPriceError = existsError(item, "pricePromo" + cntry, "E")}

                                <Row>
                                    <Cell class="form-label">{$_(cntry + "_price")}</Cell>
                                    <Textfield
                                        bind:value={item["price" + cntry]}
                                        variant="outlined"
                                        type="text"
                                        class="w-100"
                                        withTrailingIcon={hasPriceError}
                                        invalid={hasPriceError}
                                        input$maxlength={5}
                                        input$autocomplete="Nothing"
                                        input$emptyValueUndefined
                                        input$class="tescoblue-text fs-1rem"
                                        on:input={() => {
                                            validateInputPrice();
                                        }}
                                        on:change={() => {
                                            validateInputPrice();
                                        }}
                                        on:focus={(event) => {
                                            event.detail.target.select();
                                        }}
                                    >
                                        <svelte:fragment slot="trailingIcon">
                                            {#if hasPriceError}
                                                <Icon class="material-icons red-text mdc-text-field__icon mdc-text-field__icon--trailing">error</Icon>
                                            {/if}
                                        </svelte:fragment>
                                    </Textfield>
                                    <Cell class="form-label">{$_("promo_price")}</Cell>
                                    <Textfield
                                        bind:value={item["pricePromo" + cntry]}
                                        variant="outlined"
                                        type="text"
                                        class="w-100"
                                        withTrailingIcon={hasPromoPriceError}
                                        invalid={hasPromoPriceError}
                                        input$maxlength={5}
                                        input$autocomplete="Nothing"
                                        input$emptyValueUndefined
                                        input$class="tescoblue-text fs-1rem"
                                        on:input={() => {
                                            validateInputPrice();
                                        }}
                                        on:change={() => {
                                            validateInputPrice();
                                        }}
                                        on:focus={(event) => {
                                            event.detail.target.select();
                                        }}
                                    >
                                        <svelte:fragment slot="trailingIcon">
                                            {#if hasPromoPriceError}
                                                <Icon class="material-icons red-text  mdc-text-field__icon mdc-text-field__icon--trailing">error</Icon>
                                            {/if}
                                        </svelte:fragment>
                                    </Textfield>
                                    <DTInputCell
                                        colspan="1"
                                        value={item["currency" + cntry]}
                                        editable={false}
                                        numeric={false}
                                        style="width: 7rem;"
                                        class="fs-1rem tescoblue-text grey lighten-2"
                                    />
                                </Row>
                                {#if hasPriceError}
                                    <Row class="helpertext__row">
                                        <Cell></Cell>
                                        <Cell colspan={4} style="padding: 0">
                                            <div class="red-text fs-08rem">
                                                {$_(getFieldErrMsg(item, "price" + cntry, "E"))}
                                            </div>
                                        </Cell>
                                    </Row>
                                {/if}
                                {#if hasPromoPriceError}
                                    <Row class="helpertext__row">
                                        <Cell></Cell>
                                        <Cell colspan={4} style="padding: 0">
                                            <div class="red-text fs-08rem">
                                                {$_(getFieldErrMsg(item, "pricePromo" + cntry, "E"))}
                                            </div>
                                        </Cell>
                                    </Row>
                                {/if}
                            {/if}
                        {/each}

                        {#if existsError(item, "price", "E")}
                            <Row class="helpertext__row">
                                <Cell></Cell>
                                <Cell colspan={4} style="padding: 0">
                                    <div class="red-text fs-08rem">
                                        {$_(getFieldErrMsg(item, "price", "E"))}
                                    </div>
                                </Cell>
                            </Row>
                        {/if}
                    </Body>
                </DataTable>
            {/if}
        </div></Content
    >
    <Actions class="pr-4 gap-1 space-around">
        <Button
            action="close"
            class="tescored tescored-text outlined"
            on:click={() => {
                dispatch("reject");
            }}
        >
            <Label>{$_("cancel")}</Label>
        </Button>
        <Button
            action="save"
            use={[InitialFocus]}
            class={buttonSaveEnabled ? "tescoblue white-text" : "grey white-text"}
            disabled={!buttonSaveEnabled}
            on:click={() => {
                dispatch("addQSPurchase", { item: item, type: "add" });
            }}
        >
            <Label>{$_("save")}</Label>
        </Button>
    </Actions>
</Dialog>
